/**
 * LeadContactEmail - The contact email sent on a partner's leads.
 *
 * Empty means the support mailbox. Setting another address, such as a
 * support+test@ alias while a partner tests against production, lets the partner
 * flag those leads without a deploy.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { getErrorMessage } from '@/common/utils/errors'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Input } from '@/common/components/input'
import { updatePartner, type PartnerItem } from '../../api/partners'

interface LeadContactEmailProps {
  partner: PartnerItem
}

export function LeadContactEmail({ partner }: LeadContactEmailProps) {
  const { token } = useAuth()
  const { showSuccessToast, showErrorToast } = useToast()
  const queryClient = useQueryClient()
  // Null until someone edits the field, so a refreshed partner shows through
  // instead of a stale copy that Save would write back.
  const [draft, setDraft] = useState<string | null>(null)
  const savedEmail = partner.contact_email ?? ''

  const save = useMutation({
    mutationFn: (contactEmail: string | null) =>
      updatePartner(token ?? '', partner.id, { contact_email: contactEmail }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['admin', 'partners', partner.id], updated)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'partners'] })
      setDraft(null)
      showSuccessToast(`Leads now carry ${updated.contact_email ?? updated.default_contact_email}.`)
    },
    onError: (error: unknown) => showErrorToast(getErrorMessage(error)),
  })

  const trimmed = (draft ?? savedEmail).trim()
  const isChanged = draft !== null && trimmed.toLowerCase() !== savedEmail

  return (
    <Stack gap="8">
      <Text as="h2" variant="bodyLg" weight="bold">
        Contact email on leads
      </Text>
      <Text variant="bodySm" color="muted">
        {partner.display_name} records this as each lead&apos;s contact. Leave it empty to use the
        support mailbox, {partner.default_contact_email}.
      </Text>
      <Text variant="bodySm">
        Leads currently carry {partner.contact_email ?? partner.default_contact_email}.
      </Text>
      <Stack direction="row" gap="12" align="end" wrap>
        <Input
          label="Contact email"
          type="email"
          value={draft ?? savedEmail}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={partner.default_contact_email}
          disabled={save.isPending}
          optional
        />
        <Button onClick={() => save.mutate(trimmed || null)} disabled={!isChanged || save.isPending}>
          Save contact email
        </Button>
      </Stack>
    </Stack>
  )
}
